import { useEffect, useState, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import { useGenerateSummary } from "@/components/analysisModules/AnnotationExplorer/comps/Summary";
import { useGetQueryData } from "@/utils/react-query-hooks/general";

export const useGetAISummary = ({
  originalValue,
  blockValuePath,
  contentType,
  activeFilters,
  enabled = true,
  enableVisibilityFetching = false,
}) => {
  const [ summary, setSummary ] = useState("");
  const { mutate: generateSummary, status, error } = useGenerateSummary();
  
  const { ref, inView } = useInView({ 
    triggerOnce: true,
    skip: !enableVisibilityFetching 
  });

  const platformConfigs = useGetQueryData("platformConfigs");
  const isAiSummarizationEnabled = !!platformConfigs?.ai?.aiSummarization?.enable;

  const shouldFetch = enableVisibilityFetching ? inView : true;
  const shouldGenerateSummary = blockValuePath && contentType && enabled && shouldFetch && isAiSummarizationEnabled;

  const serializedFilters = useMemo(
    () => JSON.stringify(activeFilters || []),
    [activeFilters]
  );

  useEffect(() => {
    if (shouldGenerateSummary) {
      generateSummary(
        {
          dataSource: "chunks",
          activeFilters,
          findQuery: {
            "meta.valuePath": blockValuePath,
            "meta.contentType": contentType,
          },
        },
        { onSuccess: ({ data }) => setSummary(data.summary) }
      );
    }
  }, [serializedFilters, contentType, blockValuePath, shouldGenerateSummary, generateSummary]);

  const getValue = () => {
    if (!shouldGenerateSummary) {
      return originalValue;
    }
    
    if (status === 'success' && summary.length) {
      return summary;
    }
    
    return originalValue;
  };

  const isWaitingForVisibility = enableVisibilityFetching && !inView;
  const effectiveStatus = isWaitingForVisibility ? "loading" : (shouldGenerateSummary ? status : 'idle');

  const aiSummaryDisabled = !isAiSummarizationEnabled;

  return {
    value: getValue(),
    status: effectiveStatus,
    error,
    aiSummaryDisabled,
    isAISummary: shouldGenerateSummary && status === 'success' && summary.length > 0,
    ref: enableVisibilityFetching ? ref : undefined,
  };
};

